import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  IconSchool,
  IconCode,
  IconSpeakerphone,
  IconSearch,
  IconDeviceLaptop,
  IconLayout,
  IconSparkle,
} from "@tabler/icons-react";

const Hero = () => {
  const [audience, setAudience] = useState("students");

  const content = {
    students: {
      title1: "Learn In-Demand IT Skills.",
      title2: "Build Your Tech Career.",
      description:
        "Join WE3X Institute, an industry-focused IT training institute in Karaikudi, offering MERN Stack, Full Stack Development, Web Development, Java, and Digital Marketing courses. Gain practical training, hands-on project experience, and career-ready skills.",
      button: "Explore IT Courses",
      subText: "Learn. Build. Grow with WE3X.",
    },

    business: {
      title1: "Build Powerful Digital Solutions.",
      title2: "Grow Your Business with WE3X.",
      description:
        "WE3X Technologies is a software development and digital marketing company in Karaikudi, providing web development, custom software development, application development, MERN Stack solutions, SEO, and digital marketing services to help businesses build scalable digital solutions, improve online visibility, generate leads, and achieve sustainable growth.",
      button: "Start Your Project",
      subText: "Build. Grow. Succeed with WE3X.",
    },
  };

  const activeContent = content[audience];

  return (
    <>
      <section
        id="home"
        className="
        min-h-screen
        bg-[#f6f6f6]
        px-[14px]
        pb-10
        pt-[120px]
        sm:px-5
        lg:pt-[125px]
      "
      >
        {/* HERO BOX */}
        <div
          className="
          relative
          mx-auto
          flex
          min-h-[650px]
          w-full
          max-w-[1320px]
          flex-col
          overflow-hidden
          rounded-[18px]
          bg-white
        "
        >
          {/* ================================================= */}
          {/* TOP LEFT BORDER ONLY */}
          {/* ================================================= */}

          <div className="pointer-events-none absolute left-0 top-0 z-20 h-[170px] w-[300px]">
            {/* horizontal */}
            <div
              className="
              absolute
              left-0
              top-0
              h-[2px]
              w-full
              bg-gradient-to-r
              from-[#4aa8ff]
              via-[#79c1ff]
              to-transparent
            "
            />

            {/* vertical */}
            <div
              className="
              absolute
              left-0
              top-0
              h-full
              w-[2px]
              bg-gradient-to-b
              from-[#4aa8ff]
              via-[#79c1ff]
              to-transparent
            "
            />

            {/* rounded corner */}
            <div
              className="
              absolute
              left-0
              top-0
              h-[35px]
              w-[35px]
              rounded-tl-[18px]
              border-l-[2px]
              border-t-[2px]
              border-[#4aa8ff]
            "
            />
          </div>

          {/* TOP LEFT GLOW */}
          <div
            className="
            pointer-events-none
            absolute
            left-0
            top-0
            h-[320px]
            w-[390px]
          "
            style={{
              background:
                "radial-gradient(circle at top left, rgba(76,169,255,0.16) 0%, rgba(76,169,255,0.08) 35%, transparent 70%)",
            }}
          />

          {/* ================================================= */}
          {/* BOTTOM RIGHT BORDER ONLY */}
          {/* ================================================= */}

          <div className="pointer-events-none absolute bottom-0 right-0 z-20 h-[170px] w-[300px]">
            {/* horizontal */}
            <div
              className="
              absolute
              bottom-0
              right-0
              h-[2px]
              w-full
              bg-gradient-to-l
              from-[#4aa8ff]
              via-[#79c1ff]
              to-transparent
            "
            />

            {/* vertical */}
            <div
              className="
              absolute
              bottom-0
              right-0
              h-full
              w-[2px]
              bg-gradient-to-t
              from-[#4aa8ff]
              via-[#79c1ff]
              to-transparent
            "
            />

            {/* rounded corner */}
            <div
              className="
              absolute
              bottom-0
              right-0
              h-[35px]
              w-[35px]
              rounded-br-[18px]
              border-b-[2px]
              border-r-[2px]
              border-[#4aa8ff]
            "
            />
          </div>

          {/* BOTTOM RIGHT GLOW */}
          <div
            className="
            pointer-events-none
            absolute
            bottom-0
            right-0
            h-[320px]
            w-[390px]
          "
            style={{
              background:
                "radial-gradient(circle at bottom right, rgba(76,169,255,0.16) 0%, rgba(76,169,255,0.08) 35%, transparent 70%)",
            }}
          />

          {/* ================================================= */}
          {/* MAIN CONTENT */}
          {/* ================================================= */}

          <div
            className="
            relative
            z-10
            flex
            flex-1
            flex-col
            items-center
            px-6
            pb-14
            pt-10
            text-center
          "
          >
            {/* SWITCH */}
            <div
              className="
              mb-12
              flex
              items-center
              rounded-full
              border
              border-neutral-200
              bg-[#f7f7f7]
              p-[4px]
              shadow-sm
            "
            >
              {/* STUDENT */}
              <button
                type="button"
                onClick={() => setAudience("students")}
                className={`
                flex
                items-center
                gap-2
                rounded-full
                px-5
                py-2.5
                text-[13px]
                font-semibold
                transition-all
                duration-300

                ${
                  audience === "students"
                    ? "bg-[#55aef3] text-white shadow-md"
                    : "bg-transparent text-neutral-700 hover:bg-white"
                }
              `}
              >
                <IconSchool size={16} />
                For Students
              </button>

              {/* BUSINESS */}
              <button
                type="button"
                onClick={() => setAudience("business")}
                className={`
                flex
                items-center
                gap-2
                rounded-full
                px-5
                py-2.5
                text-[13px]
                font-semibold
                transition-all
                duration-300

                ${
                  audience === "business"
                    ? "bg-[#55aef3] text-white shadow-md"
                    : "bg-transparent text-neutral-700 hover:bg-white"
                }
              `}
              >
                <IconCode
                  size={16}
                  className={
                    audience === "business" ? "text-white" : "text-violet-600"
                  }
                />
                For Businesses
              </button>
            </div>

            {/* HEADING */}
            <h1
              key={`${audience}-heading`}
              className="
              animate-homeFade
              max-w-[850px]
              text-[45px]
              font-bold
              leading-[1.03]
              tracking-[-0.045em]
              text-[#111111]
              sm:text-[55px]
              lg:text-[64px]
            "
            >
              {activeContent.title1}

              <br />

              {activeContent.title2}
            </h1>

            {/* DESCRIPTION */}
            <p
              key={`${audience}-paragraph`}
              className="
              animate-homeFade
              mt-8
              max-w-[820px]
              text-[15px]
              leading-[1.8]
              text-[#555555]
              sm:text-[16px]
            "
            >
              {activeContent.description}
            </p>

            {/* CTA */}
            <Link
              to={audience === "students" ? "/institute" : "/contact"}
              className="
              mt-9
              rounded-[8px]
              bg-[#151515]
              px-8
              py-[14px]
              text-[14px]
              font-semibold
              text-white
              shadow-sm
              transition
              duration-300
              hover:-translate-y-[2px]
              hover:bg-black
              hover:shadow-lg
            "
            >
              {activeContent.button}
            </Link>

            <p className="mt-4 text-[12px] font-medium text-neutral-500">
              {activeContent.subText}
            </p>
          </div>

          {/* ================================================= */}
          {/* MARQUEE */}
          {/* ================================================= */}

          <div
            className="
    relative
    z-10
    overflow-hidden
    border-t
    border-neutral-200
    bg-white
    py-6
  "
          >
            <div className="pointer-events-none absolute inset-y-0 left-0 z-20 w-24 bg-gradient-to-r from-white to-transparent" />

            <div className="pointer-events-none absolute inset-y-0 right-0 z-20 w-24 bg-gradient-to-l from-white to-transparent" />

            <div className="we3x-home-marquee flex w-max items-center">
              {[1, 2].map((group) => (
                <div
                  key={group}
                  className="
          flex
          min-w-max
          items-center
          gap-20
          px-12
          sm:gap-24
          lg:gap-32
        "
                >
                  <div className="flex items-center gap-2 text-neutral-900">
                    <IconCode size={21} />
                    <span className="whitespace-nowrap text-[15px] font-bold">
                      Customized Software
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-neutral-600">
                    <IconSchool size={21} />
                    <span className="whitespace-nowrap text-[15px] font-bold">
                      Institute
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-neutral-700">
                    <IconSearch size={21} />
                    <span className="whitespace-nowrap text-[15px] font-bold">
                      SEO
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-neutral-700">
                    <IconSpeakerphone size={21} />
                    <span className="whitespace-nowrap text-[15px] font-bold">
                      Digital Marketing
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-neutral-900">
                    <IconDeviceLaptop size={21} />
                    <span className="whitespace-nowrap text-[15px] font-bold">
                      Web App
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-neutral-600">
                    <IconLayout size={21} />
                    <span className="whitespace-nowrap text-[15px] font-bold">
                      Landing Page
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-neutral-600">
                    <IconSparkle size={21} />
                    <span className="whitespace-nowrap text-[15px] font-bold">
                      WE3X
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <style>
          {`
          @keyframes homeMarquee {
            from {
              transform: translateX(0);
            }

            to {
              transform: translateX(-50%);
            }

          }


          .we3x-home-marquee {
            animation: homeMarquee 25s linear infinite;
          }


          .we3x-home-marquee:hover {
            animation-play-state: paused;
          }


          @keyframes homeFade {

            from {
              opacity: 0;
              transform: translateY(8px);
            }

            to {
              opacity: 1;
              transform: translateY(0);
            }

          }
          .animate-homeFade {
            animation: homeFade 0.35s ease-out;
          }
          @media (prefers-reduced-motion: reduce) {
            .we3x-home-marquee {
              animation: none;
            }
            .animate-homeFade {
              animation: none;
            }

          }

        `}
        </style>
      </section>
      <section></section>
    </>
  );
};
export default Hero;
